import { Suspense } from 'react';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';

import { Loading } from '../../../../components/Loading';
import { useAppSelector } from '../../../../reduxstore/hooks';
import { storeIdState, storeState } from '../../../../Store/atoms';
import { WebOnlineProductGroupQuery } from '../../../graphql';
import { filterVisibleProducts } from '../../../utils/filterVisibleProducts';
import { Product } from './Product';

export type ProductsListProps = {
	products: WebOnlineProductGroupQuery['webOnlineProductGroup']['products'];
	selectProduct: (id: string, name: string, product_group: string) => void;
	product_group: string;
};

export const ProductCard: React.FC<ProductsListProps> = ({
	products,
	selectProduct,
	product_group
}) => {
	const store = useRecoilValue(storeState);
	const storeId = useRecoilValue(storeIdState);

	const favoriteProducts = useAppSelector(
		state => state.accountReducer.profile.favorite_products
	);

	const unavailableProducts = useAppSelector(
		state => state.uiReducer.unavailableProductIds
	);
	const favoriteProductIds = favoriteProducts
		? favoriteProducts.map(
				(favorite: FavoriteProduct) => `${favorite.product}`
			)
		: [];
	return (
		<Suspense fallback={<Loading />}>
			<ProductContainer>
				{products
					.slice()
					.filter(
						filterVisibleProducts(storeId, store?.regionId ?? 0)
					)
					.filter(
						product =>
							!unavailableProducts.includes(parseInt(product.id))
					)
					.sort((a, b) => {
						const aPriority = a.priority || Infinity; // Assign a high value to priority 0
						const bPriority = b.priority || Infinity; // Assign a high value to priority 0

						if (aPriority === bPriority) {
							return a.name.localeCompare(b.name); // Sort by name if priorities are equal
						}
						return aPriority - bPriority; // Sort by priority
					})
					.map(({ id, name, icon }, key) => (
						<Product
							name={name}
							icon={icon}
							key={key}
							selectProduct={() =>
								selectProduct(id, name, product_group)
							}
							isFavorite={favoriteProductIds.includes(id)}
						/>
					))}
			</ProductContainer>
		</Suspense>
	);
};

const ProductContainer = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr 1fr;
	grid-gap: 48px;
	justify-content: space-between;
	margin-bottom: 48px;

	@media (max-width: 992px) {
		grid-gap: 24px;
	}
	@media (max-width: 768px) {
		grid-template-columns: 1fr 1fr;
		grid-gap: 16px;
	}
`;
